import {
	Alert,
	Box,
	Group,
	SegmentedControl,
	Switch,
	Text,
	UnstyledButton
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { CircleQuestionMark, InfoIcon } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import styles from '@/components/flow-drawer/components/flow-drawer-header/flow-drawer-header.module.css';
import { FlowDrawerHeaderActions } from '@/components/flow-drawer/components/flow-drawer-header-actions';
import useEmitter from '@/hooks/use-emitter.ts';
import useFlowStore, { FlowDrawerHeaderMode } from '@/store/flow.ts';
import { AppEvent } from '@/types/event-emitter.types.ts';

export const FlowDrawerHeader: FC = () => {
	const [isComparingViewDisabled, setIsComparingViewDisabled] = useState(false);
	const {
		flowItem,
		configs,
		setFlowDrawerViewMode,
		isFormattedView,
		library,
		isComparingMode,
		toggleComparingMode,
		groupType
	} = useFlowStore(
		useShallow((state) => ({
			flowItem: state.flowDrawer.flowItem,
			groupType: state.flowDrawer.groupType,
			isComparingMode: state.flowDrawer.isComparingMode,
			isFormattedView: state.flowDrawer.isFormattedView,
			library: state.library,
			configs: state.canvas.configs,
			setFlowDrawerViewMode: state.setFlowDrawerViewMode,
			toggleComparingMode: state.toggleComparingMode
		}))
	);

	const type = (flowItem?.source.idn ? flowItem.type : groupType) as string;

	const originalCard = useMemo(() => {
		const targetIdn = flowItem?.source.origin_idn;
		for (const { items, idn: libType, color: libColor } of library) {
			const found = items.find((i) => i.idn === targetIdn);
			if (found) {
				return { ...found, libType, libColor };
			}
		}
	}, [library, flowItem?.source.origin_idn]);

	const handleOpenDescriptionModal = () => {
		modals.open({
			title: configs[type]?.title,
			children: (
				<Box className={styles.descriptionBody}>
					<Text>{configs[type]?.description}</Text>
				</Box>
			),
			size: 'xl'
		});
	};

	const handleFlowDrawerViewModeChange = (mode: string) => {
		if (
			mode !== FlowDrawerHeaderMode.MARKDOWN &&
			mode !== FlowDrawerHeaderMode.FORMATTED
		) {
			return;
		}

		setFlowDrawerViewMode(mode);
	};

	const handleToggleComparingMode = () => {
		if (!flowItem?.source.idn) {
			return;
		}

		if (flowItem.source.is_from_library) {
			toggleComparingMode();
			return;
		}

		toggleComparingMode(
			originalCard
				? {
						...originalCard,
						idn: `${originalCard.idn}:library`
					}
				: undefined
		);
	};

	useEmitter(AppEvent.IS_FLOW_ITEM_FORM_CHANGED, ({ isFormChanged }) => {
		setIsComparingViewDisabled(isFormChanged);
	});
	return (
		<Group>
			<Group
				align="flex-start"
				w="100%"
				wrap="nowrap"
			>
				<Group
					justify="flex-start"
					w="100%"
				>
					<Box
						className={styles.title}
						style={{ '--type-color': configs[type]?.color }}
					>
						<Text
							component="span"
							fz={13}
							lh="xs"
						>
							{configs[type]?.title}
						</Text>

						<UnstyledButton onClick={handleOpenDescriptionModal}>
							<CircleQuestionMark
								color={configs[type]?.color}
								height={14}
								opacity={0.5}
								width={14}
							/>
						</UnstyledButton>
					</Box>

					{flowItem?.source.version && (
						<Box className={styles.version}>
							<Text
								component="span"
								fz={13}
								lh="xs"
							>
								Original - ver {flowItem.source.version}
							</Text>
						</Box>
					)}

					{flowItem?.source.idn && (
						<SegmentedControl
							classNames={{
								root: styles.segmentControlRoot,
								label: styles.segmentControlLabel
							}}
							data={[
								{ label: 'Markdown', value: FlowDrawerHeaderMode.MARKDOWN },
								{ label: 'Formatted', value: FlowDrawerHeaderMode.FORMATTED }
							]}
							disabled={isComparingMode}
							onChange={handleFlowDrawerViewModeChange}
							value={
								isFormattedView
									? FlowDrawerHeaderMode.FORMATTED
									: FlowDrawerHeaderMode.MARKDOWN
							}
						/>
					)}
				</Group>
				{flowItem?.source.idn ? (
					<Group w="100%">
						<Group
							justify="flex-end"
							w="100%"
						>
							<Switch
								checked={isComparingMode}
								className={styles.noWrap}
								disabled={isComparingViewDisabled}
								label="Comparison View"
								onChange={handleToggleComparingMode}
							/>
							<FlowDrawerHeaderActions originalCard={originalCard} />
						</Group>
					</Group>
				) : undefined}
			</Group>

			{!isComparingMode && flowItem?.source?.is_from_library ? (
				<Box className={styles.nonEditableWarningContainer}>
					<Alert
						classNames={{ wrapper: styles.wrapper }}
						color="yellow"
						icon={<InfoIcon />}
						p={8}
						title="This is a library item and cannot be edited. If you want to modify it, first clone this library item."
						variant="light"
					/>
				</Box>
			) : undefined}
		</Group>
	);
};
